import { useCallback, useEffect, useState } from 'react';
import type { Product } from '../wysiwyg/packageUtils';
import { getProduct } from './useProducts';

type LookupResult = {
  organizationId: number;
  productId: number;
  attempt: number;
  product: Product | null;
  failed: boolean;
};

// A rejected lookup gets one automatic retry. Persistent failures remain retryable on demand.
export default function useHistoricalProductOverride(
  organizationId: number | undefined,
  productId: number | null,
  enabled: boolean,
) {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<LookupResult | null>(null);
  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    setResult(null);
    if (!enabled || !organizationId || productId == null) return;

    let cancelled = false;
    const complete = (product: Product | null) => {
      if (cancelled) return;
      setResult({ organizationId, productId, attempt, product, failed: !product });
    };

    const load = async () => {
      for (let automaticAttempt = 0; automaticAttempt < 2; automaticAttempt += 1) {
        try {
          const product = await getProduct(organizationId, productId);
          complete(product && Number(product.id) === productId ? product as Product : null);
          return;
        } catch {
          if (cancelled) return;
          if (automaticAttempt === 1) complete(null);
        }
      }
    };

    void load();

    return () => { cancelled = true; };
  }, [organizationId, productId, enabled, attempt]);

  // Do not expose the previous organization's/selection's result, even before effect cleanup.
  const current = enabled && result?.organizationId === organizationId &&
    result?.productId === productId && result?.attempt === attempt ? result : null;

  const isLoading = Boolean(enabled && organizationId && productId != null && !current);

  return { product: current?.product || null, failed: current?.failed || false, isLoading, retry };
}
